import ToolTipComponent from "@CubeComponents/html/tooltip";
import { convertDate } from "@Services/luxonHelper";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { getUrlFromField, buildConcatenatedValue } from "@Features/forms";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { FormTemplateField, QuestionField } from "@ServerTypes/forms/forms.schema";

// Image and Table Section cases need to be handled later when all grids are moved to SlickGrid

interface FileData {
  name?: string;
  field?: string;
}

interface UrlValue {
  URL?: string;
  displayText?: string;
}

/** A grid row as the default `getValueFromRow` accessor below reads it. */
type CustomFieldRow = { customFields?: Record<string, unknown> } | undefined;

type CustomFieldsRecord = Record<string, unknown>;

/**
 * Cell values arrive here straight from the grid as stored form answers, i.e. with no type at all,
 * so the shapes the formatters need are read structurally instead of asserted: a stored answer that
 * is not the expected shape formats as blank rather than crashing the cell.
 */
const toFileData = (value: unknown): FileData => {
  if (typeof value !== "object" || value === null) return {};
  return {
    name: "name" in value && typeof value.name === "string" ? value.name : undefined,
    field: "field" in value && typeof value.field === "string" ? value.field : undefined,
  };
};

const toUrlValue = (value: unknown): UrlValue => {
  if (typeof value !== "object" || value === null) return {};
  return {
    URL: "URL" in value && typeof value.URL === "string" ? value.URL : undefined,
    displayText: "displayText" in value && typeof value.displayText === "string" ? value.displayText : undefined,
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const isCustomFieldRow = (row: unknown): row is NonNullable<CustomFieldRow> =>
  isRecord(row) && (!("customFields" in row) || isRecord(row.customFields));

/**
 * Rows reach `_valueGetter` / `formatter` from SlickGrid untyped, so they are checked here rather
 * than asserted into the accessor's row type. Every row that is an object still reaches the accessor
 * exactly as it did before: one whose `customFields` is not the record the type describes (a null
 * column in the stored document, say) is passed on with only that key cleared, since a custom accessor
 * may be reading a different key off it — Assets passes one that reads `projectLevelCustomFields`.
 * Only a non-object row resolves to `undefined`, which is what the accessor's `?.` chain made of it.
 */
const toCustomFieldRow = (row: unknown): CustomFieldRow => {
  if (isCustomFieldRow(row)) return row;
  if (!isRecord(row)) return undefined;
  return { ...row, customFields: undefined };
};

const fileFormatter = (val: unknown) => {
  const container = document.createElement("div");
  if (!Array.isArray(val) || val.length === 0) {
    container.textContent = "-";
    return container;
  }

  container.className = "flex pe-3 overflow-auto gap-4 w-full h-full";

  // `Array.isArray` on an `unknown` narrows to `any[]`; widen on assignment so each entry is read
  // through `toFileData` rather than trusted.
  const files: unknown[] = val;

  const root = createRoot(container);
  root.render(
    <>
      {files.map((entry) => {
        const data = toFileData(entry);
        return (
          <span
            key={data.name || data.field}
            className="flex gap-3 border-solid border-2 border-button_light w-fit rounded-lg pe-2 items-center"
          >
            <span className="w-14 h-10 flex object-contain">{getFileIcon(data.name)}</span>
            <span>{data.name}</span>
          </span>
        );
      })}
    </>,
  );

  return container;
};

const dateFormatter = (question: QuestionField, val: unknown) => {
  const container = document.createElement("div");
  const root = createRoot(container);
  const dateType = question.displayAs === "dateOnly" ? "NormalDate" : "dateForTooltip";
  const content = convertDate(Number(val), dateType);
  root.render(
    <ToolTipComponent position="bottom" content={content}>
      {content}
    </ToolTipComponent>,
  );
  return container;
};

const urlFormatter = (val: unknown) => {
  const { URL: href, displayText } = toUrlValue(val);
  const container = document.createElement("div");
  container.className = "text-gradient_color cursor-pointer underline underline-offset-2";
  container.onclick = (e: MouseEvent) => {
    e.stopPropagation();
    const url = getUrlFromField(href ?? "");
    window.open(url);
  };
  const root = createRoot(container);
  root.render(<span>{displayText ?? ""}</span>);
  return container;
};

export const getSubColumns = (
  section: FormTemplateField,
  getFieldKey: (questionId: string) => string = (questionId: string) => `customFields.${questionId}`,
  getValueFromRow: (row: CustomFieldRow, questionId: string) => unknown = (row: CustomFieldRow, questionId: string) =>
    row?.customFields?.[questionId],
) => {
  const { questionFields = [], sectionHeading, _id } = section;
  return questionFields.map((question: QuestionField) => {
    const questionId = question._id;
    const fieldKey = getFieldKey(questionId);
    const columnGroup = sectionHeading;
    const typeMap: Record<string, string> = {
      "single-line": "Text",
      "multi-line": "Text",
      "date-time": "Date",
      "multi-select": "MultiSelect",
    };

    const filterType = typeMap[question.fieldType] || "";

    // Concatenate has no stored value of its own — it is a live formula over other fields. Rebuild
    // it here from the row's sibling values (read through the same accessor) so the grid column
    // shows the current concatenation and reflects later edits, matching how it is displayed
    // elsewhere via buildConcatenatedValue.
    const resolveValue = (row: unknown) => {
      const gridRow = toCustomFieldRow(row);
      if (question.fieldType === "concatenate") {
        const valueById = new Map<string, string>();
        (question.selectedFields ?? []).forEach((sourceId: string) => {
          const sourceValue = getValueFromRow(gridRow, sourceId);
          valueById.set(sourceId, sourceValue === undefined || sourceValue === null ? "" : String(sourceValue));
        });
        return buildConcatenatedValue(question.selectedFields, valueById, question.seperator);
      }
      return getValueFromRow(gridRow, questionId);
    };

    const column: Record<string, unknown> = {
      id: fieldKey,
      field: fieldKey,
      name: question.questionValue,
      columnGroup,
      minWidth: 150,
      allowFiltering: true,
      allowSorting: true,
      filterType: filterType,
      initiallyHidden: true,
      columnGroupId: _id,
      _valueGetter: (args: CustomFieldsRecord) => {
        const value = resolveValue(args);
        if (value === undefined || value === "" || value === null || (Array.isArray(value) && value.length === 0)) {
          return "-";
        } else if (question.fieldType === "multi-select" && Array.isArray(value)) {
          return value.join(", ");
        } else if (question.fieldType === "url") {
          return (value as UrlValue).displayText;
        } else if (question.fieldType === "fileUpload" && Array.isArray(value)) {
          return value;
        } else if (question.fieldType === "image" && Array.isArray(value)) {
          return value;
        }
        return value;
      },
      formatter: (_data: number, _value: number, _a: unknown, _b: unknown, params: unknown) => {
        const val = resolveValue(params);
        if (val === undefined || val === "" || val === null || (Array.isArray(val) && val.length === 0)) {
          return "-";
        } else if (question.fieldType === "multi-select" && Array.isArray(val)) {
          return val.join(", ");
        } else if (question.fieldType === "date-time") {
          return dateFormatter(question, val);
        } else if (question.fieldType === "url") {
          return urlFormatter(val);
        } else if (question.fieldType === "image") {
          return val;
        } else if (question.fieldType === "fileUpload") {
          return fileFormatter(val);
        }
        return val;
      },
    };

    if (["image", "fileUpload", "look-up", "multi-select"].includes(question.fieldType)) {
      column.allowFiltering = false;
      column.allowSorting = false;
    } else if (["single-line", "multi-line", "single-select", "url", "concatenate"].includes(question.fieldType)) {
      column.filterType = "Text";
    } else if (["number", "autoNumber"].includes(question.fieldType)) {
      column.filterType = "Number";
    } else if (question.fieldType == "date-time") {
      column.filterType = "Date";
    }

    return column;
  });
};
